import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatCurrency, formatDate, MONTH_NAMES } from "@/lib/format";
import MonthSelector from "@/components/MonthSelector";
import MetricCard from "@/components/MetricCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AreaChart, Area, ComposedChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Wallet, Banknote, TrendingUp, CheckCircle2, Coffee, ShoppingBag, Smartphone, Scale,
  AlertTriangle, ArrowRight, PlusCircle, Info, Building2, Store as StoreIcon,
} from "lucide-react";

const now = new Date();
const tooltipStyle = { backgroundColor: "#131832", border: "1px solid #2A364F", borderRadius: 8, color: "#F8FAFC", fontSize: 12 };

function AlertBanner({ alerts }) {
  if (!alerts?.length) return null;
  return (
    <div className="space-y-2" data-testid="dashboard-alerts">
      {alerts.map((a, i) => (
        <div key={i} className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm font-medium ${a.level === "danger" ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-amber-500/40 bg-amber-500/10 text-amber-400"}`}>
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" /><span>{a.message}</span>
        </div>
      ))}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="mb-4 font-display text-base font-bold">{title}</h3>
      <div className="h-56 w-full">{children}</div>
    </div>
  );
}

// ---------------- CONSOLIDATED ----------------
function Consolidated() {
  const navigate = useNavigate();
  const { setStoreId } = useStore();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/consolidated").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="py-20 text-center text-muted-foreground">Caricamento…</div>;
  const t = d.totals;
  const openStore = (id) => { setStoreId(id); navigate("/"); };
  return (
    <div className="space-y-6" data-testid="consolidated-dashboard">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Petrol Giuly — Tutti i punti vendita</h1>
        <p className="text-sm text-muted-foreground">Panoramica consolidata di {d.num_stores} sedi · {MONTH_NAMES[d.month]} {d.year}</p>
      </div>
      <AlertBanner alerts={d.alerts} />
      <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-card to-secondary p-6 sm:p-8" data-testid="consolidated-hero">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><Wallet className="h-4 w-4" /> Totale contanti da versare (tutte le sedi)</div>
        <div className="mt-3 font-display text-5xl font-black tabular-nums text-primary sm:text-6xl" data-testid="consolidated-da-versare">{formatCurrency(t.da_versare)}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <MetricCard label="Incassi contanti oggi" value={t.incassi_giorno} icon={Banknote} accent="yellow" testId="c-inc-day" />
        <MetricCard label="Versamenti oggi" value={t.versamenti_giorno} icon={TrendingUp} accent="blue" testId="c-ver-day" />
        <MetricCard label="Incassi mese" value={t.incassi_mese} icon={Banknote} accent="yellow" />
        <MetricCard label="Versamenti mese" value={t.versamenti_mese} icon={TrendingUp} accent="blue" />
        <MetricCard label="Riconciliazioni" value={t.riconciliazioni} icon={CheckCircle2} accent="green" />
        <MetricCard label="Bar" value={t.bar} icon={Coffee} accent="orange" />
        <MetricCard label="Shop" value={t.shop} icon={ShoppingBag} accent="orange" />
        <MetricCard label="Ricariche" value={t.ricariche} icon={Smartphone} accent="blue" />
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg border border-border bg-secondary/40 px-4 py-3"><span className="text-muted-foreground">Sedi: </span><b>{d.kpi.num_stores}</b></div>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3"><span className="text-muted-foreground">Sopra soglia: </span><b className="text-destructive">{d.kpi.sopra_soglia}</b></div>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3"><span className="text-muted-foreground">Dati mancanti oggi: </span><b className="text-amber-400">{d.kpi.mancanti}</b></div>
      </div>
      <div>
        <h2 className="mb-3 font-display text-lg font-bold">Suddivisione per punto vendita</h2>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {d.per_store.map((s) => (
            <button key={s.id} onClick={() => openStore(s.id)} data-testid={`consolidated-store-${s.id}`}
              className="flex w-full items-center justify-between border-b border-border/50 px-5 py-4 text-left transition-colors hover:bg-secondary/40">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold">{s.name} {s.over_threshold && <span className="ml-1 rounded bg-destructive/20 px-1.5 py-0.5 text-[10px] font-bold text-destructive">SOGLIA</span>}</div>
                  <div className="text-xs text-muted-foreground">{s.brand} · mese: incassi {formatCurrency(s.incassi_mese)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`font-display text-lg font-black tabular-nums ${s.over_threshold ? "text-destructive" : "text-primary"}`}>{formatCurrency(s.da_versare)}</div>
                <div className="text-[11px] text-muted-foreground">da versare</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- SINGLE STORE ----------------
function SingleDashboard() {
  const navigate = useNavigate();
  const { storeId, currentStore } = useStore();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [init, setInit] = useState(false);

  const load = useCallback(async (y, m) => {
    const params = { store_id: storeId };
    if (y && m) { params.year = y; params.month = m; }
    const { data } = await api.get("/dashboard", { params });
    setData(data); setYear(data.year); setMonth(data.month);
  }, [storeId]);

  useEffect(() => { setInit(false); }, [storeId]);
  useEffect(() => { if (!init) load().then(() => setInit(true)); }, [init, load]);

  const changeMonth = (y, m) => { setYear(y); setMonth(m); load(y, m); };
  const openBreakdown = async () => {
    const { data } = await api.get("/breakdown", { params: { store_id: storeId } });
    setBreakdown(data); setBreakdownOpen(true);
  };

  if (!data) return <div className="py-20 text-center text-muted-foreground" data-testid="dashboard-loading">Caricamento dashboard…</div>;
  const c = data.cards;
  const over = data.da_versare_totale > data.soglia;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary"><StoreIcon className="h-4 w-4" /> {currentStore?.name}</div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Mese di riferimento: {MONTH_NAMES[month]} {year}</p>
        </div>
        <MonthSelector year={year} month={month} onChange={changeMonth} />
      </div>

      <AlertBanner alerts={data.alerts} />

      <button onClick={openBreakdown} data-testid="hero-contanti-da-versare-card"
        className={`group relative w-full overflow-hidden rounded-2xl border p-6 text-left transition-colors duration-200 sm:p-8 ${over ? "border-destructive/50 bg-destructive/10" : "border-primary/40 bg-gradient-to-br from-card to-secondary"}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><Wallet className="h-4 w-4" /> Contanti attualmente da versare</div>
          <span className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">Da dove deriva? <Info className="h-3.5 w-3.5" /></span>
        </div>
        <div className={`mt-4 font-display text-5xl font-black tabular-nums tracking-tight sm:text-6xl ${over ? "text-destructive" : "text-primary"}`} data-testid="hero-da-versare-value">{formatCurrency(data.da_versare_totale)}</div>
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <span>Soglia di allerta: {formatCurrency(data.soglia)}</span>
          {over && <span className="rounded-full bg-destructive/20 px-2 py-0.5 text-xs font-bold text-destructive">SUPERATA</span>}
        </div>
      </button>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="today-incasso-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Incasso di oggi</div>
          <div className="mt-2 font-display text-2xl font-extrabold tabular-nums">{formatCurrency(data.today.incasso)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{formatDate(data.today.date)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5" data-testid="today-versato-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Versato oggi</div>
          <div className="mt-2 font-display text-2xl font-extrabold tabular-nums text-[#5B8DEF]">{formatCurrency(data.today.versato)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{data.today.has_data ? "Giornata registrata" : "Nessun dato oggi"}</div>
        </div>
        <div className="flex items-center justify-center rounded-xl border border-primary/40 bg-card p-5">
          <Button className="h-14 w-full gap-2 text-base font-bold" onClick={() => navigate("/nuovo-giorno")} data-testid="dashboard-nuovo-giorno-btn"><PlusCircle className="h-6 w-6" /> Inserisci Giornata</Button>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-bold">Riepilogo {MONTH_NAMES[month]}</h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <MetricCard label="Incassi contanti mese" value={c.incassi_contanti} icon={Banknote} accent="yellow" testId="card-incassi" />
          <MetricCard label="Versamenti effettuati" value={c.versamenti} icon={TrendingUp} accent="blue" testId="card-versamenti" />
          <MetricCard label="Da versare (totale)" value={c.da_versare} icon={Wallet} accent={over ? "red" : "yellow"} testId="card-da-versare" />
          <MetricCard label="Riconciliato mese" value={c.riconciliato} icon={CheckCircle2} accent="green" testId="card-riconciliato" />
          <MetricCard label="Bar" value={c.bar} icon={Coffee} accent="orange" testId="card-bar" />
          <MetricCard label="Shop" value={c.shop} icon={ShoppingBag} accent="orange" testId="card-shop" />
          <MetricCard label="Ricariche" value={c.ricariche} icon={Smartphone} accent="blue" testId="card-ricariche" />
          <MetricCard label="Differenza / Scostamento" value={c.differenza} icon={Scale} accent={c.differenza < 0 ? "red" : "green"} testId="card-differenza" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-border bg-secondary/40 px-4 py-3"><span className="text-muted-foreground">Saldo iniziale mese: </span><span className="font-semibold tabular-nums">{formatCurrency(data.saldo_iniziale)}</span></div>
          <div className="rounded-lg border border-border bg-secondary/40 px-4 py-3"><span className="text-muted-foreground">Saldo finale mese: </span><span className="font-semibold tabular-nums">{formatCurrency(data.saldo_finale)}</span></div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Andamento incassi giornalieri">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.chart_days}>
              <defs><linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C6A15A" stopOpacity={0.5} /><stop offset="100%" stopColor="#C6A15A" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A364F" /><XAxis dataKey="day" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} width={40} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
              <Area type="monotone" dataKey="incasso" stroke="#C6A15A" strokeWidth={2} fill="url(#gInc)" name="Incasso" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Incassi vs Versamenti">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.chart_days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A364F" /><XAxis dataKey="day" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} width={40} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="incasso" fill="#C6A15A" name="Incasso" radius={[3, 3, 0, 0]} /><Bar dataKey="versato" fill="#2A3B9E" name="Versato" radius={[3, 3, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Andamento saldo DA VERSARE">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.chart_days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A364F" /><XAxis dataKey="day" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} width={40} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
              <Line type="monotone" dataKey="da_versare" stroke="#5B8DEF" strokeWidth={2.5} dot={false} name="Da versare" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <div className="flex flex-col justify-center rounded-xl border border-border bg-card p-5">
          <h3 className="mb-2 font-display text-base font-bold">Dettaglio giornaliero</h3>
          <p className="mb-4 text-sm text-muted-foreground">Apri il registro completo con i totali del mese.</p>
          <Button variant="secondary" className="gap-2" onClick={() => navigate("/registro")} data-testid="goto-registro-btn">Vai al Registro <ArrowRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={breakdownOpen} onOpenChange={setBreakdownOpen}>
        <DialogContent data-testid="breakdown-dialog">
          <DialogHeader><DialogTitle>Ricostruzione del saldo da versare</DialogTitle></DialogHeader>
          {breakdown && (
            <div className="space-y-1 font-mono text-sm">
              <Row label="Totale incassi contanti" value={breakdown.totale_incassi} sign="+" />
              <Row label="Totale versamenti" value={breakdown.totale_versamenti} sign="−" />
              <Row label="Totale riconciliazioni" value={breakdown.totale_riconciliazioni} sign="±" />
              <div className="my-2 border-t border-border" />
              <div className="flex items-center justify-between rounded-lg bg-primary/10 px-3 py-3">
                <span className="font-display font-bold uppercase tracking-wide">Contanti da versare</span>
                <span className="font-display text-xl font-black text-primary">{formatCurrency(breakdown.da_versare)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, sign }) {
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-muted-foreground"><span className="mr-2 inline-block w-4 text-center font-bold text-foreground">{sign}</span>{label}</span>
      <span className="tabular-nums">{formatCurrency(value)}</span>
    </div>
  );
}

export default function Dashboard() {
  const { storeId, isSuperAdmin, loading } = useStore();
  if (loading) return <div className="py-20 text-center text-muted-foreground">Caricamento…</div>;
  if (storeId === "all" && isSuperAdmin) return <Consolidated />;
  if (!storeId) return <div className="py-20 text-center text-muted-foreground">Nessun punto vendita assegnato.</div>;
  return <SingleDashboard />;
}
